import { useState } from 'react';
import { Button } from '@/components/ui/button';
import type { ElectionState, StateRace } from '../types';
import { BallotCandidateCard } from './BallotCandidateCard';
import { ConfirmVoteModal } from './ConfirmVoteModal';

interface KioskBallotProps {
  state: ElectionState;
  race: StateRace;
  positionTitle: string;
  voterName: string;
  submitting: boolean;
  error: string | null;
  /** candidateId null = blank vote */
  onVote: (candidateId: string | null) => void;
  onCancel: () => void;
}

type Choice = { id: string | null; name: string };

export function KioskBallot({ state, race, positionTitle, voterName, submitting, error, onVote, onCancel }: KioskBallotProps) {
  const [choice, setChoice] = useState<Choice | null>(null);
  const cards = (race.candidate_ids ?? [])
    .map((id) => state.candidates.find((c) => c.id === id))
    .filter((c): c is NonNullable<typeof c> => !!c);

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6 p-6">
      <div className="text-center">
        <p className="text-lg text-muted-foreground">Votando: <strong className="text-foreground">{voterName}</strong></p>
        <h1 className="text-4xl font-bold">{positionTitle}</h1>
        <p className="text-xl text-muted-foreground">Ronda {race.round_number} · Toque la foto de su candidato</p>
      </div>

      {error && <p className="rounded-xl bg-destructive/10 p-4 text-center text-xl text-destructive">{error}</p>}

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {cards.map((c) => (
          <BallotCandidateCard
            key={c.id}
            name={c.full_name ?? '—'}
            photoFilename={c.photo_filename}
            disabled={submitting}
            onSelect={() => setChoice({ id: c.id, name: c.full_name ?? '—' })}
          />
        ))}
        {race.allow_blank && (
          <BallotCandidateCard blank name="Voto en blanco" disabled={submitting} onSelect={() => setChoice({ id: null, name: 'Voto en blanco' })} />
        )}
      </div>

      <div className="text-center">
        <Button variant="ghost" size="lg" className="text-lg" onClick={onCancel} disabled={submitting}>Cancelar y volver a buscar</Button>
      </div>

      {choice && (
        <ConfirmVoteModal
          choiceName={choice.name}
          submitting={submitting}
          onConfirm={() => onVote(choice.id)}
          onCancel={() => setChoice(null)}
        />
      )}
    </div>
  );
}
